import { Order, PostNoteCommandInput, UpdateItemDetailsCommandInput, UpdateSubLineItemCommandInput } from "@amzn/ito-client";
import { useState } from "react";

import { getHopOrdersFromItems, isCancellationEligible, isTerminalStatus } from "../helpers/cancellation-helpers";
import { HardwareOrderStatus } from "../interfaces/hardware-models";
import { getITOClient, ITOConsumer } from "./hooks-helpers";
import { AMZNHAM_CONSUMER } from "../interfaces/constants";

/**
 * Hook that orchestrates the order cancellation flow:
 * 1. Validates HOP eligibility from the already-loaded order items
 * 2. Stores the cancellationReason in order.details first
 * 3. Cancels all non-terminal sub-line items and items
 *
 * The reason must be stored before items are cancelled because
 * ITO's bubble-up sets the order status to CANCELLED once all items
 * are cancelled, which triggers EventBridge. The OrderProcessor needs
 * the cancellationReason to be present when it processes the event.
 */
export function useCancelOrder() {
    const [eligibilityError, setEligibilityError] = useState<Error | undefined>(undefined);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [data, setData] = useState<any>(undefined);

    async function cancelOrder(order: Order, reason: string, csrfToken: string, amznhamOrder?: Order): Promise<void> {
        setEligibilityError(undefined);
        setData(undefined);

        // Check HOP eligibility from the already-loaded order items
        const hopOrders = getHopOrdersFromItems(order);
        if (!isCancellationEligible(hopOrders)) {
            setEligibilityError(
                new Error("Order cannot be cancelled — HOP order has progressed beyond Ready for Export.")
            );
            return;
        }

        const items = (order as any).items ?? [];
        if (items.length === 0) return;

        setIsSubmitting(true);
        try {
            const { client, requestHandler } = getITOClient();
            requestHandler.pushHeader("x-csrf-token", csrfToken);

            // Step 1: Store the cancellationReason in the amznham order details BEFORE cancelling items.
            // This ensures the reason is available when the OrderProcessor processes the
            // EventBridge event triggered by bubble-up after all items are cancelled.
            // We merge into the existing details because updateOrderStatus replaces the entire object.
            if (amznhamOrder?.orderId) {
                const { client: amznhamClient, requestHandler: amznhamRequestHandler } = getITOClient(AMZNHAM_CONSUMER as ITOConsumer);
                amznhamRequestHandler.pushHeader("x-csrf-token", csrfToken);
                const currentDetails = (amznhamOrder.details as any) ?? {};
                await amznhamClient.updateOrderStatus({
                    orderId: amznhamOrder.orderId,
                    details: { ...currentDetails, cancellationReason: reason },
                });
            }

            // Step 2: Cancel all non-terminal sub-line items and items.
            // Sub-line items must be cancelled first — bubble-up propagates to items, then to order.
            const itemsToCancel = items.filter((item: any) => item.itemId && !isTerminalStatus(item.status ?? ""));

            for (const item of itemsToCancel) {
                // Cancel non-terminal sub-line items first
                const subsToCancelProps = (item.subLineItems ?? [])
                    .filter((sub: any) => sub.id && !isTerminalStatus(sub.status ?? ""))
                    .map((sub: any): UpdateSubLineItemCommandInput => ({
                        orderId: order.orderId!,
                        itemId: item.itemId!,
                        id: sub.id,
                        subLineItem: { ...sub, status: HardwareOrderStatus.CANCELLED },
                    }));

                for (const props of subsToCancelProps) {
                    await client.updateSubLineItem(props);
                }

                // Then cancel the item itself
                await client.updateItemDetails({
                    orderId: order.orderId,
                    itemId: item.itemId,
                    status: HardwareOrderStatus.CANCELLED,
                });
            }

            setData({ success: true });

            // Step 3: Post an internal note documenting the cancellation reason and source.
            client.postNote({
                taxonomyId: order.orderId!,
                comment: `Order cancelled from IT-HAM. Reason: ${reason}`,
                tags: ["internal"],
            } as PostNoteCommandInput).catch((noteError) => {
                console.error(`Failed to post internal cancellation note for order ${order.orderId}:`, noteError);
            });
        } catch (e) {
            setEligibilityError(new Error(`Failed to cancel order: ${(e as Error).message}`));
        }
        setIsSubmitting(false);
    }

    return { isSubmitting, error: eligibilityError, cancelOrder, data };
}
